// PROVISION-IN-PRODUCT: a DE's set-up as the product ran it on create (budget, agent key,
// memory, space, runtime, CoS digest, first task), whether its runtime has taken its keys, and
// where its bridge listens. Managers only (the route is manager-gated); re-running is a visible
// icon and only does what is missing. A DE that existed before shows "not run yet" until asked.
import { Badge, Button, InfoTip, SettingsSection, toast } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCwIcon, WrenchIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime } from "../format";
import { problemText } from "./de-status";

const STEP_LABEL: Record<string, string> = {
  budget: "Budget",
  agent_key: "Agent key",
  memory: "Memory",
  space: "Space",
  runtime: "Runtime",
  cos_digest: "CoS digest",
  first_task: "First task",
};

const STATE_BADGE = {
  done: { label: "Done", variant: "success" },
  skipped: { label: "Skipped", variant: "neutral" },
  failed: { label: "Failed", variant: "danger" },
  pending: { label: "Pending", variant: "warning" },
} as const;

export const provisioningKey = (co: string, de: string) => ["de-provisioning", co, de] as const;

export function DeProvisioningSection({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const qc = useQueryClient();
  const manager = isManager(company);
  const q = useQuery({
    queryKey: provisioningKey(company.id, de.membership_id),
    queryFn: () =>
      api.client.digitalEmployees.provisioning.get({
        params: { co: company.id, id: de.membership_id },
      }),
    enabled: manager,
  });
  const rerun = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.provision({
        params: { co: company.id, id: de.membership_id },
      }),
    onSuccess: () => {
      toast.add({ title: "Set-up queued" });
      void qc.invalidateQueries({ queryKey: provisioningKey(company.id, de.membership_id) });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Could not queue the set-up"), type: "error" }),
  });
  if (!manager || !q.data) return null;
  const v = q.data;
  const notRun = v.origin === "existing" && v.provisioned_at === null;
  return (
    <SettingsSection
      icon={WrenchIcon}
      title={
        <span className="inline-flex items-center gap-1">
          Set-up
          <InfoTip label="About set-up">
            What CompanyOS set up when this DE was created. Re-running only adds what is missing;
            settings come from your platform's defaults.
          </InfoTip>
        </span>
      }
      actions={
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Run set-up again"
          title="Run set-up again"
          disabled={rerun.isPending}
          onClick={() => rerun.mutate()}
        >
          <RefreshCwIcon />
        </Button>
      }
      data-slot="de-provisioning"
    >
      {notRun ? (
        <p className="text-xs text-muted-foreground">
          Not run yet (created before set-up existed).
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-border">
        {v.steps.map((s) => {
          const b = STATE_BADGE[s.state];
          return (
            <li key={s.step} className="flex items-center gap-2 py-1.5 text-xs">
              <span className="w-24 shrink-0 font-medium">{STEP_LABEL[s.step] ?? s.step}</span>
              <Badge variant={b.variant}>{b.label}</Badge>
              <span className="min-w-0 flex-1 truncate text-muted-foreground" title={s.detail}>
                {s.detail}
              </span>
              {s.at ? (
                <span className="shrink-0 text-2xs text-faint">{formatDateTime(s.at)}</span>
              ) : null}
            </li>
          );
        })}
        <li className="flex items-center gap-2 py-1.5 text-xs">
          <span className="w-24 shrink-0 font-medium">Runtime keys</span>
          <Badge variant={v.credentials_claimed ? "success" : "neutral"}>
            {v.credentials_claimed ? "Taken" : "Waiting"}
          </Badge>
        </li>
        <li className="flex items-center gap-2 py-1.5 text-xs">
          <span className="w-24 shrink-0 font-medium">Wake</span>
          <Badge variant={v.bridge ? "success" : "neutral"}>{v.bridge ? "Push" : "Polls"}</Badge>
          {v.bridge ? (
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {v.bridge.container ?? v.bridge.url}
            </span>
          ) : null}
        </li>
      </ul>
      {v.connect ? <ConnectSteps api={api} company={company} de={de} connect={v.connect} /> : null}
    </SettingsSection>
  );
}

const CONNECT_LABEL: Record<string, string> = {
  seat: "Seat",
  agent_spec: "Agent spec",
  agent_key: "Agent key",
  endpoint: "Endpoint",
  instance: "Container",
  tool_grant: "Tools",
  bridge_unit: "Bridge",
};

const CONNECT_BADGE = {
  ...STATE_BADGE,
  waiting: { label: "Waiting", variant: "warning" },
} as const;

type ConnectView = NonNullable<
  Awaited<ReturnType<Api["client"]["digitalEmployees"]["provisioning"]["get"]>>["connect"]
>;

/**
 * DE-CONNECT: the DE's connection to its agent container, step by step; "Resume" asks again
 * with the recorded placement (only what is missing is done).
 */
function ConnectSteps({
  api,
  company,
  de,
  connect,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  connect: ConnectView;
}) {
  const qc = useQueryClient();
  const resume = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.connect({
        params: { co: company.id, id: de.membership_id },
        body: {},
      }),
    onSuccess: () => {
      toast.add({ title: "Connect queued" });
      void qc.invalidateQueries({ queryKey: provisioningKey(company.id, de.membership_id) });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "Could not queue the connect"), type: "error" }),
  });
  return (
    <div className="mt-3 flex flex-col gap-1" data-slot="de-connect">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium">Container</span>
        <span className="min-w-0 flex-1 truncate text-muted-foreground">
          {connect.instance_id} / {connect.profile_name}
          {connect.adopt ? " (adopted)" : ""}
        </span>
        <Badge variant={connect.connected_at ? "success" : "warning"}>
          {connect.connected_at ? "Connected" : "In progress"}
        </Badge>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Resume connect"
          title="Resume connect"
          disabled={resume.isPending}
          onClick={() => resume.mutate()}
        >
          <RefreshCwIcon />
        </Button>
      </div>
      <ul className="flex flex-col divide-y divide-border">
        {connect.steps.map((s) => {
          const b = CONNECT_BADGE[s.state];
          return (
            <li key={s.step} className="flex items-center gap-2 py-1.5 text-xs">
              <span className="w-24 shrink-0 font-medium">{CONNECT_LABEL[s.step] ?? s.step}</span>
              <Badge variant={b.variant}>{b.label}</Badge>
              <span className="min-w-0 flex-1 truncate text-muted-foreground" title={s.detail}>
                {s.detail}
              </span>
              {s.at ? (
                <span className="shrink-0 text-2xs text-faint">{formatDateTime(s.at)}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
